/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { HomeIcon, ExitIcon, MixIcon, ResetIcon, Cross1Icon, FileTextIcon, ChatBubbleIcon } from '@radix-ui/react-icons';
import { Code } from 'lucide-react';
// import { SiJira } from 'react-icons/si';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, authSelector } from '../../features/auth/authSlice';
import { useTheme } from '../Theme/ThemeProvider';
import { FeedbackDialog } from './FeedbackDialog';
import { ThemeDialog } from '../Dialogs/ThemeDialog';
import { RootState } from '../../store/store';
import { setChatMode, setRagChatType, clearRagHistory, clearaskChatHistory } from '../../store/chatModeSlice';
import { clearTranslationData, clearUploadedFiles } from '../../store/chatSlice';
import { getSavedBubbleColor, getBubbleStyle } from '../../utils/bubbleColors';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: 'main' | 'chat';
  onClearChat?: () => void;
  isMobile?: boolean;
  onShowWhatsNew?: () => void;
}

export function MobileMenu({ isOpen, onClose, currentPage = 'main', onClearChat, isMobile = false, onShowWhatsNew }: MobileMenuProps) {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const { accent, theme } = useTheme();
  const isDark = theme.startsWith('dark');
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const [showFeedback, setShowFeedback] = useState(false);
  const [activeTab, setActiveTab] = useState<'Ask Chat' | '3m-knowledge'>(chatModeState.mode === '3M Knowledge' ? '3m-knowledge' : 'Ask Chat');

  // Reactively track the user bubble color (updated via ThemeDialog)
  const [userBubbleColor, setUserBubbleColor] = useState(() => getSavedBubbleColor());
  useEffect(() => {
    const sync = () => setUserBubbleColor(getSavedBubbleColor());
    window.addEventListener('user-bubble-color-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('user-bubble-color-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Only render for mobile devices
  if (!isOpen) return null;

  const getGradientColor = (accentColor: string) => {
    if (accentColor === 'purple') return '139, 92, 246';
    if (accentColor === 'blue') return '59, 130, 246';
    if (accentColor === 'green') return '16, 185, 129';
    if (accentColor === 'gold') return '245, 158, 11';
    return '209, 4, 17';
  };

  const gradientColor = getGradientColor(accent);

  // Get user initials from MSAL account
  const account = instance.getActiveAccount();
  const userName = account?.name || account?.username || '';
  const initials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n: string) => n[0].toUpperCase())
    .join('');

  const handleLogout = () => {
    if (isMobile) {
      instance.logoutRedirect();
    } else {
      instance.logoutPopup();
    }
    dispatch(logout());
    onClose();
  };

  const handleNavigateHome = () => {
    if (chatModeState.isStreaming) return;
    dispatch(setChatMode('Ask Chat'));
    dispatch(setRagChatType(null));
    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    dispatch(clearUploadedFiles());
    dispatch(clearTranslationData());
    if (onClearChat) onClearChat();
    onClose();
  };

  const handleRagSelect = (chatType: 'Everyday Insights' | 'Legal' | 'DevOps' | 'JIRA') => {
    if (chatModeState.isStreaming) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    dispatch(setChatMode('3M Knowledge'));
    dispatch(setRagChatType(chatType));
    onClose();
  };

  const handleNewRagChat = () => {
    if (chatModeState.isStreaming) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    if (onClearChat) onClearChat();
    onClose();
  };

  const handleSelect3MKnowledge = () => {
    if (chatModeState.isStreaming) return;
    setActiveTab('3m-knowledge');
    dispatch(setChatMode('3M Knowledge'));
    if (!chatModeState.ragChatType) {
      dispatch(clearRagHistory());
      dispatch(clearTranslationData());
      dispatch(clearUploadedFiles());
      dispatch(setRagChatType('Everyday Insights'));
    }
  };

  // RAG items for the 3M Knowledge tab
  const ragItems = [
    {
      key: 'Everyday Insights' as const,
      label: 'Everyday Insights',
      icon: (_isActive: boolean) => <ChatBubbleIcon width={22} height={22} />,
    },
    {
      key: 'Legal' as const,
      label: '3M Legal',
      icon: (_isActive: boolean) => <FileTextIcon width={22} height={22} />,
    },
    {
      key: 'DevOps' as const,
      label: 'DevOps',
      icon: (_isActive: boolean) => <Code size={22} strokeWidth={2} />,
    },
    // TODO: JIRA
    // {
    //   key: 'JIRA' as const,
    //   label: 'JIRA',
    //   icon: (isActive: boolean) => React.createElement(SiJira as any, {
    //     size: 22,
    //     style: {
    //       color: isActive ? '#ffffff' : 'var(--color-text)',
    //     },
    //   }),
    // },
  ];

  const tabActive = 'rounded-full px-5 py-1.5 text-sm font-semibold transition-all';
  const tabInactive = 'rounded-full px-5 py-1.5 text-sm font-medium transition-all opacity-60';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[20100] transition-opacity"
        onClick={onClose}
      />

      {/* Full-screen menu overlay */}
      <div
        className="fixed inset-0 z-[20200] flex flex-col"
        style={{ backgroundColor: 'var(--color-bg)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <h2 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>Menu</h2>
          <button
            aria-label="Close Menu"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 focus:outline-none"
            style={{ color: 'var(--color-text)' }}
          >
            <Cross1Icon width={22} height={22} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center justify-center gap-1 px-4 py-3">
          <div
            className="flex items-center gap-1 p-1 rounded-full"
            style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}
          >
            <button
              disabled={chatModeState.isStreaming}
              onClick={() => { setActiveTab('Ask Chat'); handleNavigateHome(); }}
              className={`${activeTab === 'Ask Chat' ? tabActive : tabInactive} ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={
                activeTab === 'Ask Chat'
                  ? { backgroundColor: 'var(--color-text)', color: 'var(--color-bg)', display: 'flex', alignItems: 'center', gap: '6px' }
                  : { color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '6px' }
              }
            >
              <span className="translate-y-[2px]">Ask Chat</span>
            </button>
            <button
              disabled={chatModeState.isStreaming}
              onClick={handleSelect3MKnowledge}
              className={`${activeTab === '3m-knowledge' ? tabActive : tabInactive} ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={
                activeTab === '3m-knowledge'
                  ? { backgroundColor: 'var(--color-text)', color: 'var(--color-bg)' }
                  : { color: 'var(--color-text)' }
              }
            >
              <span className="translate-y-[2px]">3M Knowledge</span>
            </button>
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          {/* ── Ask Chat Tab ── */}
          {activeTab === 'Ask Chat' && (
            <nav className="space-y-1 mt-1">
              {/* Home / Ask Chat */}
              <button
                onClick={handleNavigateHome}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-colors text-left"
                style={{
                  backgroundColor: currentPage === 'main'
                    ? 'var(--color-accent)'
                    : 'transparent',
                  color: currentPage === 'main'
                    ? '#ffffff'
                    : 'var(--color-text)',
                }}
              >
                <HomeIcon width={22} height={22} />
                <span className="text-base font-medium translate-y-[2px]">Home</span>
                {currentPage === 'main' && (
                  <span
                    className="ml-auto w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
                  />
                )}
              </button>
            </nav>
          )}

          {/* ── 3M Knowledge Tab ── */}
          {activeTab === '3m-knowledge' && (
            <nav className="space-y-1 mt-1">
              {/* New Chat — only shown on chat page when a RAG mode is active, matching desktop Menu.tsx */}
              {currentPage === 'chat' && chatModeState.ragChatType !== null && onClearChat && (
                <>
                  <button
                    disabled={chatModeState.isStreaming}
                    onClick={handleNewRagChat}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/8 transition-colors text-left ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ color: 'var(--color-text)' }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="12" y1="8" x2="12" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="9" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span className="text-base translate-y-[2px]">New Chat</span>
                  </button>
                  <div className="my-1 mx-2 border-t border-[var(--border-color)] opacity-40" />
                </>
              )}
              {ragItems.map((item) => {
                const isActive = chatModeState.ragChatType === item.key;
                return (
                  <button
                    key={item.key}
                    disabled={chatModeState.isStreaming}
                    onClick={() => handleRagSelect(item.key)}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-colors text-left ${chatModeState.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor: isActive
                        ? 'var(--color-accent)'
                        : 'transparent',
                      color: isActive
                        ? '#ffffff'
                        : 'var(--color-text)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor =
                        isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    <span
                      className="flex-shrink-0"
                      style={{ color: isActive ? '#ffffff' : 'var(--color-text)' }}
                    >
                      {item.icon(isActive)}
                    </span>
                    <span className="text-base font-medium translate-y-[2px]">{item.label}</span>
                    {isActive && (
                      <span
                        className="ml-auto w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
                      />
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          {/* Divider */}
          <div className="my-2 mx-2 border-t border-[var(--border-color)] opacity-40" />

          {/* Theme Settings and What's New: visible for both tabs */}
          <div className="mt-2">
            <div className="relative" data-tutorial="theme-button">
              <button
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/8 transition-colors text-left"
                style={{ color: 'var(--color-text)' }}
              >
                <MixIcon width={22} height={22} />
                <span className="text-base translate-y-[2px]">Theme Settings</span>
              </button>
              <div className="absolute inset-0 bg-transparent">
                <ThemeDialog />
              </div>
            </div>

            {onShowWhatsNew && (
              <button
                onClick={() => { onShowWhatsNew(); onClose(); }}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/8 transition-colors text-left"
                style={{ color: 'var(--color-text)' }}
              >
                <svg width="22" height="22" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
                <span className="text-base translate-y-[2px]">What's New</span>
              </button>
            )}
          </div>

          {/* Feedback */}
          <button
            onClick={() => setShowFeedback(true)}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/8 transition-colors text-left"
            style={{ color: 'var(--color-text)' }}
          >
            <ResetIcon width={22} height={22} />
            <span className="text-base translate-y-[2px]">Feedback</span>
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/8 transition-colors text-left"
            style={{ color: 'var(--color-text)' }}
          >
            <ExitIcon width={22} height={22} />
            <span className="text-base translate-y-[2px]">Logout</span>
          </button>
        </div>

        {/* User info footer */}
        {userName && (
          <div className="px-4 py-3 border-t border-[var(--border-color)] flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-sm font-bold"
              style={auth.photo ? undefined : getBubbleStyle(userBubbleColor, isDark ? 'dark' : 'light')}
            >
              {auth.photo ? (
                <img
                  src={auth.photo}
                  alt={userName}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                initials || '?'
              )}
            </div>
            <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
              {userName}
            </span>
          </div>
        )}

        {/* Gradient bottom border */}
        <div
          className="w-full h-1"
          style={{
            background: `radial-gradient(ellipse at center, rgb(${gradientColor}) 0%, rgb(${gradientColor}) 40%, rgba(${gradientColor}, 0.3) 60%, rgba(${gradientColor}, 0) 80%)`,
          }}
        />
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog
        showFeedback={showFeedback}
        setShowFeedback={setShowFeedback}
        fullScreen={isMobile}
      />
    </>
  );
}
